import React, { useMemo, useState, useRef, useEffect } from "react";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import ChatWindow from "./chat/ChatWindow";
import { deepMerge } from "./utils/deepMerge";
import schema from "./schema.json";
import uiSchema from "./ui_schema.json";
import { exportPdf } from "./utils/exportPdf";

function buildWsUrl() {
  const env = (process.env.REACT_APP_WS_URL || "").trim();
  if (env) return env;

  const wsProto = window.location.protocol === "https:" ? "wss" : "ws";
  // IMPORTANT: use the current site host (fixes the localhost/production bug)
  return `${wsProto}://${window.location.host}/ws`;
}

const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#fafafa" },
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif',
  },
});

export default function App() {
  const initialFormData = useMemo(() => ({}), []);
  const [formData, setFormData] = useState<any>(initialFormData);
  const [showPreview, setShowPreview] = useState(false);

  const wsUrl = useMemo(() => buildWsUrl(), []);

  const wsCmdRef = useRef<((cmd: string) => void) | null>(null);
  const [isFormalizing, setIsFormalizing] = useState(false);
  const formalizeResolverRef = useRef<null | (() => void)>(null);

  // Avoid stale exportPdf(formData)
  const formDataRef = useRef<any>(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const isMobile = useMediaQuery("(max-width: 900px)");

  const onFormPatch = (patch: any) => {
    setFormData((prev: any) => deepMerge(prev, patch));
  };

  const resetLocal = () => {
    setFormData(initialFormData);
    setShowPreview(false);
  };

  const formalizeNow = () =>
    new Promise<void>((resolve) => {
      if (!wsCmdRef.current) return resolve();

      setIsFormalizing(true);
      formalizeResolverRef.current = resolve;

      wsCmdRef.current("__FORMALIZE__");

      // Safety timeout so UI never gets stuck
      window.setTimeout(() => {
        if (formalizeResolverRef.current) {
          setIsFormalizing(false);
          formalizeResolverRef.current?.();
          formalizeResolverRef.current = null;
        }
      }, 25000);
    });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      <Box
        sx={{
          height: { xs: "auto", md: "100vh" },
          minHeight: "100vh",
          p: { xs: 1.25, md: 2 },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "420px 1fr" },
          gap: 2,
          alignItems: "stretch",
          overflow: { xs: "visible", md: "hidden" }, // desktop: prevent page scroll; panels scroll internally
          boxSizing: "border-box",
        }}
      >
        {/* CHAT PANEL - fixed height on desktop, independent scroll */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            height: { xs: "60vh", md: "calc(100vh - 32px)" }, // 32px approx grid padding (top+bottom)
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ChatWindow
            wsUrl={wsUrl}
            onFormPatch={onFormPatch}
            onResetLocal={resetLocal}
            onWsCommandReady={(sendCmd) => (wsCmdRef.current = sendCmd)}
            onAssistantText={(text) => {
              // server sends "Fertig. Texte wurden formalisiert."
              if (text.includes("Fertig.")) {
                setIsFormalizing(false);
                formalizeResolverRef.current?.();
                formalizeResolverRef.current = null;
              }
            }}
          />
        </Paper>

        {/* FORM PANEL - fixed height on desktop, independent scroll */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            p: { xs: 1.25, md: 2 },
            overflow: "auto",
            minHeight: 0,
            height: { xs: "auto", md: "calc(100vh - 32px)" },
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.5 }}
          >
            <Stack spacing={0.25}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Formular
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Demo: Freitext im Chat → Felder werden befüllt. Vorschau/PDF → Texte werden formalisiert.
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={{ pt: 0.25 }}
            >
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                disabled={isFormalizing}
                onClick={async () => {
                  await formalizeNow();
                  await new Promise((r) => setTimeout(r, 0));
                  setShowPreview(true);
                }}
              >
                {isFormalizing ? "Formuliere…" : "Vorschau"}
              </Button>

              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={isFormalizing}
                onClick={async () => {
                  await formalizeNow();
                  await new Promise((r) => setTimeout(r, 0));
                  exportPdf(formDataRef.current);
                }}
              >
                {isFormalizing ? "Formuliere…" : "PDF"}
              </Button>

              <IconButton
                aria-label="Reset"
                onClick={() => {
                  resetLocal();
                  wsCmdRef.current?.("__RESET_FORM__");
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Stack>
          </Stack>

          {/* IMPORTANT: merge onChange so editing one field doesn't wipe others */}
          <Form
            schema={schema as any}
            uiSchema={uiSchema as any}
            formData={formData}
            validator={validator}
            onChange={(e) => setFormData((prev: any) => deepMerge(prev, e.formData))}
            liveOmit={false}
            omitExtraData={false}
          />

          <Dialog
            open={showPreview}
            onClose={() => setShowPreview(false)}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Typography sx={{ fontWeight: 800 }}>Read-only Preview</Typography>
              <Chip size="small" label="JSON" />
            </DialogTitle>
            <DialogContent dividers>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 13, margin: 0 }}>
                {JSON.stringify(formData, null, 2)}
              </pre>
            </DialogContent>
          </Dialog>
        </Paper>
      </Box>
    </ThemeProvider>
  );
}
